import auth from "@/src/server/clients/tri-auth-client";
import { LoginResponse } from "@onetakesolutions/cargogo-contract-web/TriAuthService_pb";
import authStore from "@/src/store/authStore";
import selectOptionsStore from "@/src/store/selectOptionsStore";
import { resetAuthSessionInvalidation } from "@/src/util/auth-session";
import { resetClientSessionState } from "@/src/util/reset-client-session";
import { getErrorMessage, getHttpError, getRpcError } from "@/src/util/common";
import { HttpError } from "@/src/util/HttpError";
import { OTSError } from "@/src/util/OTSError";
import { DubboError } from "@apachedubbo/dubbo";
import { create } from "zustand";

interface LoginState {
  loading: boolean;
  success?: boolean;
  msg?: string;
  response?: LoginResponse;
  request: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResponse>;
}

export const store = create<LoginState>()((set) => ({
  loading: false,
  request: async (email: string, password: string, rememberMe?: boolean) => {
    return new Promise(async (resolve, reject) => {
      set(() => ({ loading: true, success: undefined, msg: undefined }));
      try {
        const res = await auth.login({
          email,
          password,
          rememberMe,
        } as Parameters<typeof auth.login>[0]);
        if (res.success && res.errorCode === 0 && res.data) {
          resetAuthSessionInvalidation();
          await resetClientSessionState();
          await authStore.getState().set(res.data.accessToken, res.data.refreshToken, rememberMe);
          await selectOptionsStore.getState().reset();
          set(() => ({
            loading: false,
            success: true,
            response: res,
          }));
          resolve(res);
        } else {
          const error = getHttpError(res.errorCode, res.message);
          set(() => ({ loading: false, success: false, msg: error.message }));
          reject(error);
        }
      } catch (error: unknown) {
        if (error instanceof DubboError) {
          const e = DubboError.from(error);
          const rpcError = getRpcError(e);
          set(() => ({ loading: false, success: false, msg: e.message }));
          reject(rpcError);
        } else if (error instanceof OTSError) {
          set(() => ({ loading: false, success: false, msg: error.message }));
          reject(error);
        } else if (error instanceof HttpError) {
          set(() => ({ loading: false, success: false, msg: error.message }));
          reject(error);
        } else if (error instanceof Error) {
          const msg = error.message;
          set(() => ({ loading: false, success: false, msg: msg }));
          reject(msg);
        } else {
          const msg = getErrorMessage(0);
          set(() => ({ loading: false, success: false, msg: msg }));
          reject(msg);
        }
      }
    });
  },
}));

export default store;
